import React from "react";
import { Link } from "react-router-dom";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { ArrowUpRight, Check, Phone, Mail } from "lucide-react";
import Header from "../components/site/Header";
import Seo from "../components/site/Seo";
import Footer from "../components/site/Footer";
import CTAButton from "../components/site/CTAButton";
import Photo from "../components/site/Photo";
import ShootPlanner from "../components/site/ShootPlanner";
import QuoteForm from "../components/site/QuoteForm";
import ScrollReveal from "../components/site/ScrollReveal";
import { SisterServices } from "../components/site/ServiceSections";
import InternalRail from "../components/site/InternalRail";
import { blogsForService } from "../data/internalLinks";

export default function MenuPhotography() {
  return (
    <div className="min-h-screen bg-ink text-bone">
      <Seo />
      <Header />
      <Hero />
      <WhyMenuPhotosMatter />
      <BeforeAfter />
      <WhatWePhotograph />
      <PlannerBlock />
      <MelbourneSection />
      <SisterServices exclude="menu" />
      <InternalRail
        eyebrow="Related reading · The Journal"
        headline="More on"
        italic="menu photography."
        theme="ink"
        cols={3}
        items={blogsForService("menu-photography")}
      />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}

// ─── 1. HERO ──────────────────────────────────────────
const Hero = () => (
  <section className="relative min-h-[100svh] overflow-hidden bg-ink">
    <div className="absolute inset-0">
      <Photo
        slug="hero-spread-01"
        alt="Menu photography Melbourne — restaurant catering spread"
        priority
        ratio="auto"
        className="!relative h-full w-full"
      />
    </div>
    <div className="absolute inset-0 bg-gradient-to-b from-ink/75 via-ink/40 to-ink" />
    <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/30 to-transparent" />

    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10 min-h-[100svh] flex flex-col pt-24 sm:pt-28 lg:pt-32 pb-10 lg:pb-12">
      <div className="hidden sm:flex items-center justify-between text-bone/70 mb-8 lg:mb-12">
        <p className="text-xs uppercase tracking-wider2 font-bold">
          <Link to="/" className="opacity-60 hover:opacity-100">Home</Link>
          <span className="text-ember mx-3">/</span>
          <span>Menu Photography Melbourne</span>
        </p>
        <p className="hidden lg:block text-xs uppercase tracking-wider2 font-semibold">
          Melbourne · VIC
        </p>
      </div>

      <div className="flex-1 flex flex-col justify-center max-w-5xl">
        <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 sm:mb-7 font-bold">
          Menu Photography Melbourne · Restaurants · Cafes · Takeaway
        </p>

        <h1
          className="font-display tracking-editorial leading-[0.92]"
          style={{ fontSize: "clamp(2.5rem, 7.5vw, 6.5rem)" }}
        >
          Menu photography that
          <br />
          makes food look{" "}
          <span className="italic font-light text-ember">worth ordering.</span>
        </h1>

        <p className="mt-6 sm:mt-8 max-w-2xl text-base sm:text-lg md:text-xl leading-relaxed text-bone/85">
          Professional menu photography for Melbourne restaurants, cafés,
          takeaway shops and hospitality venues. Shot on-location at your
          venue, delivered ready for Uber Eats, your website and Google.
        </p>

        <p className="mt-7 max-w-xl">
          <span className="inline-block bg-ember text-bone font-bold text-sm md:text-base uppercase tracking-wider2 px-4 py-2.5">
            Menu Photos From Only $10 Per Image
            <span className="text-bone/70 ml-1">*</span>
          </span>
        </p>
        <p className="mt-3 text-xs text-bone/55 max-w-md leading-relaxed">
          * Volume tier — selected larger menu shoots. 15-image minimum. Enquire
          for a tailored quote based on your menu size.
        </p>

        <div className="mt-8 sm:mt-10 flex flex-wrap gap-3">
          <a href="#enquiry" className="w-full sm:w-auto">
            <CTAButton variant="primary" size="lg" className="w-full sm:w-auto">Get A Shoot Quote</CTAButton>
          </a>
          <a href="#planner" className="w-full sm:w-auto">
            <CTAButton variant="ghost" size="lg" arrow={false} className="w-full sm:w-auto">Plan My Menu Shoot</CTAButton>
          </a>
        </div>
      </div>

      <div className="mt-10 lg:mt-12 grid grid-cols-3 gap-4 md:gap-10 max-w-3xl border-t border-bone/15 pt-6">
        {[
          ["Min. shoot", "15 images"],
          ["Turnaround", "5–7 days"],
          ["Coverage", "All Melbourne"],
        ].map(([k, v]) => (
          <div key={k}>
            <p className="text-[10px] sm:text-xs uppercase tracking-wider2 text-ember mb-1.5 font-bold">{k}</p>
            <p className="font-sans font-bold text-base sm:text-lg md:text-xl leading-tight tracking-tight">{v}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── 2. WHY MENU PHOTOS MATTER ────────────────────────
const WhyMenuPhotosMatter = () => (
  <section className="relative bg-ink text-bone py-24 lg:py-36 overflow-hidden">
    <div className="absolute inset-0 grain opacity-50 pointer-events-none" />
    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            Why menu photography Melbourne restaurants invest in
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Your menu sells food.
            <br />
            <span className="italic font-light text-ember">So should the photos.</span>
          </h2>

          <div className="mt-12 grid gap-7 text-bone/85 leading-relaxed text-base md:text-lg">
            <ScrollReveal>
              <p>
                Restaurant menu photography isn&apos;t a portfolio shoot — it&apos;s
                an operational asset. A good image library lifts <strong className="text-bone">add-to-cart on Uber Eats,
                DoorDash and Menulog,</strong> sharpens your Google Business Profile,
                and makes your website look like the restaurant you actually
                run.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={120}>
              <p>
                Customers decide in seconds. The first photo they see on a
                delivery app, on Instagram, or on your <strong className="text-bone">digital menu</strong> sets
                their expectation of price, quality and experience — long
                before they order or walk through the door.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <p>
                We shoot for that reality. Real plating, real lighting, real
                dishes — sized and cropped specifically for delivery apps,
                online ordering, websites, social and digital menu screens.
                One on-location day, one consistent set of images, everywhere
                they need to be.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={280}>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 pt-4">
                {[
                  "First impressions on delivery apps",
                  "Higher tap-through and add-to-cart",
                  "Google Business Profile imagery",
                  "Website and online ordering",
                  "Digital menus & QR code menus",
                  "Social, EDM and paid ads",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <Check size={16} strokeWidth={2} className="text-ember mt-1 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </ScrollReveal>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-5 space-y-3 md:space-y-5 lg:mt-4">
          <Photo slug="hero-raj-kachori-08" alt="Restaurant menu photography Melbourne" ratio="4/5" sizes="(min-width: 1024px) 35vw, 100vw" />
          <div className="grid grid-cols-2 gap-3 md:gap-5">
            <Photo slug="hero-laddu-220" alt="Food menu photography Melbourne" ratio="1/1" sizes="20vw" />
            <Photo slug="hero-gulab-jamun-00" alt="Cafe menu photography Melbourne" ratio="1/1" sizes="20vw" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ─── 3. BEFORE / AFTER ────────────────────────────────
const BAComparison = ({ slug, beforeCaption, afterCaption }) => (
  <div className="grid grid-cols-2 gap-px bg-ink/20 border border-ink/15">
    <div className="relative aspect-[4/5] overflow-hidden">
      <Photo slug={slug} alt="Before — typical phone snap" ratio="4/5" sizes="50vw" />
      <div className="absolute inset-0 pointer-events-none" style={{ backdropFilter: "saturate(0.45) brightness(0.7) blur(2.5px)", WebkitBackdropFilter: "saturate(0.45) brightness(0.7) blur(2.5px)" }} />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/75 via-transparent to-ink/15 pointer-events-none" />
      <div className="absolute top-4 left-4">
        <span className="bg-ink text-bone text-[10px] uppercase tracking-wider2 font-bold px-2.5 py-1.5">Before · Phone snap</span>
      </div>
      <div className="absolute bottom-4 left-4 right-4">
        <p className="font-display italic text-bone text-base md:text-lg tracking-editorial">{beforeCaption}</p>
      </div>
    </div>
    <div className="relative aspect-[4/5] overflow-hidden">
      <Photo slug={slug} alt="After — on-location professional menu photography" ratio="4/5" sizes="50vw" />
      <div className="absolute top-4 left-4">
        <span className="bg-ember text-bone text-[10px] uppercase tracking-wider2 font-bold px-2.5 py-1.5">After · On-location shoot</span>
      </div>
      <div className="absolute bottom-4 left-4 right-4">
        <p className="font-display italic text-bone text-base md:text-lg tracking-editorial drop-shadow-lg">{afterCaption}</p>
      </div>
    </div>
  </div>
);

const BeforeAfter = () => (
  <section className="bg-bone text-ink py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-12 mb-14 lg:mb-16 items-end">
        <div className="col-span-12 lg:col-span-6">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            Same dish · different result
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            What customers see
            <br />
            <span className="italic font-light text-ember">vs. what we shoot.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-6 lg:pb-3">
          <p className="text-char text-base md:text-lg leading-relaxed mb-5">
            Most restaurant menu photos online are still phone snaps under
            harsh kitchen lights. The food doesn&apos;t change — the lighting,
            texture, colour balance and appetite appeal do. And so does the
            order rate.
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2.5 text-sm md:text-base text-char">
            {[
              "Proper professional lighting",
              "Texture you can almost taste",
              "True-to-life colour balance",
              "Crops that work on every app",
            ].map((b) => (
              <li key={b} className="flex items-start gap-2.5">
                <Check size={14} strokeWidth={2} className="text-ember mt-1 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="space-y-8 md:space-y-12">
        <BAComparison slug="hero-gulab-jamun-00" beforeCaption="Flat. Cold. Skipped." afterCaption="The same gulab jamun, lit properly." />
        <BAComparison slug="hero-raj-kachori-08" beforeCaption="Lost in the menu." afterCaption="Top of the order, every time." />
      </div>

      <p className="mt-10 text-xs uppercase tracking-wider2 text-char-mute max-w-2xl font-medium">
        Before frames are a simulation of typical phone-on-table photography to
        illustrate the contrast. Both images are real on-location shoots.
      </p>
    </div>
  </section>
);

// ─── 4. WHAT WE PHOTOGRAPH ────────────────────────────
const CATEGORIES = [
  { n: "01", title: "Hero dishes", slug: "menu-malai-tikka", desc: "Signature plates that anchor the menu — the dish customers come back for. Shot to be the first thing seen on every channel." },
  { n: "02", title: "Curries & mains", slug: "menu-butter-chicken", desc: "Saucy, layered dishes that demand serious lighting. We compose them to show ingredients and texture without looking staged." },
  { n: "03", title: "Drinks & beverages", slug: "menu-falooda", desc: "Cocktails, lassis, mocktails, coffee — shot with the same care as the food. Condensation, garnish, glassware on-brand." },
  { n: "04", title: "Desserts & sweets", slug: "hero-gulab-jamun-00", desc: "Macro detail, hidden layers, pour shots. Desserts sell on social — we shoot them ready to share." },
  { n: "05", title: "Indian & South Asian", slug: "menu-lamb-dosa", desc: "Dosas, biryanis, curries, chaats — we work regularly with Indian, Sri Lankan and Nepalese restaurants across Melbourne." },
  { n: "06", title: "Café & breakfast food", slug: "hero-spread-01", desc: "Plated breakfasts, all-day brunch, café classics. Bright, daylit, honest — exactly how a café actually looks." },
  { n: "07", title: "Street food & chaat", slug: "menu-chinese-bhel-03", desc: "Towers, layers, chaats and snack boards. The colours sell themselves — we just light them properly." },
  { n: "08", title: "Takeaway & delivery", slug: "menu-veg-crispy", desc: "Packaged-as-served photography for delivery menus. Same dish photographed for both dine-in and Uber Eats crops." },
];

const WhatWePhotograph = () => (
  <section className="bg-ink text-bone py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 mb-14 lg:mb-20 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            What we photograph · Menu categories
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Every dish on your
            <br />
            <span className="italic font-light text-ember">menu, properly shot.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-bone/75 text-base md:text-lg leading-relaxed max-w-md">
            From a hero plate to your full delivery menu — burgers, pizza,
            curries, drinks, desserts, café food, takeaway packaging. All shot
            on-location, in one day, by the same photographer.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-bone/10 border border-bone/10">
        {CATEGORIES.map((c) => (
          <article key={c.n} className="bg-ink hover:bg-[#161613] transition-colors p-5 md:p-6 flex flex-col">
            <Photo slug={c.slug} alt={`${c.title} — Menu photography Melbourne`} ratio="4/5" sizes="(min-width: 1024px) 22vw, 50vw" />
            <div className="mt-5 flex flex-col flex-1">
              <span className="font-display italic text-2xl text-ember leading-none">{c.n}</span>
              <h3 className="font-display text-xl md:text-2xl tracking-editorial leading-tight mt-2.5">{c.title}</h3>
              <p className="mt-3 text-sm text-bone/70 leading-relaxed flex-1">{c.desc}</p>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

// ─── 5. PLANNER BLOCK ─────────────────────────────────
const PlannerBlock = () => (
  <div className="bg-bone">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10 pt-20 lg:pt-28 pb-0">
      <div className="max-w-3xl">
        <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 font-bold">
          Plan it now · Interactive estimator
        </p>
        <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5vw, 4rem)" }}>
          Get an indicative price
          <br />
          <span className="italic font-light text-ember">in three clicks.</span>
        </h2>
        <p className="mt-6 text-base md:text-lg text-char leading-relaxed">
          Drag the slider for your menu size, tick what you need, see your
          estimate. Hand-confirmed by phone within one business day. Final
          quote depends on your venue and dish prep.
        </p>
      </div>
    </div>
    <ShootPlanner />
  </div>
);

// ─── 6. MELBOURNE HOSPITALITY ─────────────────────────
const SUBURBS = [
  ["Melbourne CBD", "City restaurants, hotel kitchens, hospitality groups"],
  ["Brunswick", "Cafés, wine bars, neighbourhood restaurants"],
  ["Richmond", "Bridge Road and Swan Street venues, high-end dining"],
  ["Carlton", "Lygon Street restaurants and Italian institutions"],
  ["Footscray", "West-side food scene — Vietnamese, Ethiopian, Indian"],
  ["Glen Waverley", "Asian dining strip, family restaurants"],
  ["Dandenong", "South-east hospitality — Afghan, Indian, Sri Lankan"],
  ["Fitzroy", "Brunswick Street venues and inner-north bars"],
];

const MelbourneSection = () => (
  <section className="bg-ink text-bone py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16">
        <div className="col-span-12 lg:col-span-5 lg:sticky lg:top-32 self-start">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            Melbourne hospitality · Mobile coverage
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5vw, 4.5rem)" }}>
            Made for Melbourne
            <br />
            <span className="italic font-light text-ember">restaurants &amp; cafés.</span>
          </h2>
          <p className="mt-8 text-bone/80 leading-relaxed max-w-md text-base md:text-lg">
            A mobile menu photographer working with restaurants, cafés,
            takeaway shops and hospitality venues across Greater Melbourne.
            We come to you — kitchens in Footscray, Indian and Asian dining in
            Glen Waverley and Dandenong, café strips in Brunswick and Carlton,
            CBD and Richmond hotels.
          </p>
          <a href="#enquiry" className="inline-block mt-10">
            <CTAButton variant="ghost" size="md">Check coverage &amp; pricing</CTAButton>
          </a>
        </div>

        <div className="col-span-12 lg:col-span-7 space-y-px bg-bone/10 border border-bone/10">
          {SUBURBS.map(([name, sub]) => (
            <div key={name} className="bg-ink hover:bg-[#161613] transition-colors p-6 md:p-8 flex items-baseline justify-between gap-6 group">
              <div>
                <h3 className="font-display text-2xl md:text-3xl tracking-editorial group-hover:text-ember transition-colors">
                  Menu Photography {name}
                </h3>
                <p className="mt-2 text-sm md:text-base text-bone/65 leading-relaxed">{sub}</p>
              </div>
              <ArrowUpRight size={18} strokeWidth={1.6} className="text-bone/40 group-hover:text-ember group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0 mt-2" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ─── 7. FAQ ───────────────────────────────────────────
const FAQS = [
  { q: "How much does menu photography cost in Melbourne?", a: "Volume-tier pricing: $30 per image for 15–20 images, $25 per image for 21–99 images, and $10 per image once you reach 100+ images (minimum spend $1,000). Most café and restaurant menu shoots fall between $540 and $1,000 depending on the menu size." },
  { q: "How should I prepare for a menu shoot?", a: "We'll send a short prep checklist after you book — typically: have all dishes prepped fresh, set aside a 1–2 hour quiet window per 10 dishes, clear a clean prep bench for shooting, and brief one kitchen team member to plate during the shoot. We bring lighting, surfaces and props." },
  { q: "How many dishes can you shoot in one day?", a: "A typical full day on-location covers 25–60 dishes. Smaller refreshes (15–25 dishes) are a 3–4 hour shoot. Anything over 60 dishes splits across two shorter days to keep food and team fresh." },
  { q: "How long until I get the photos back?", a: "Edited, web and print-ready files are delivered within 5–7 business days. Urgent 3-day turnarounds are available — just mention the deadline on the call." },
  { q: "Do you shoot for Uber Eats, DoorDash and Menulog?", a: "Yes. Every menu shoot includes square plus landscape crops, consistent white-balance across dishes, and files named to match your menu items. Files are sized and ready to upload to Uber Eats, DoorDash, Menulog and your Google Business Profile." },
  { q: "Which Melbourne suburbs do you cover?", a: "All of Greater Melbourne — CBD, Carlton, Fitzroy, Brunswick, Richmond, South Yarra, St Kilda, Prahran, Footscray, Point Cook, Tarneit, Werribee, Sunshine, Epping, Craigieburn, Preston, Dandenong, Glen Waverley, Clayton and Springvale. Strongest networks across the western suburbs." },
];

const FAQ = () => (
  <section className="bg-bone text-ink py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16 mb-12 lg:mb-16 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            Common questions · Menu photography Melbourne
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Pricing, prep
            <br />
            <span className="italic font-light text-ember">and turnaround.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-char text-base md:text-lg leading-relaxed mb-5">
            If your question isn&apos;t answered below, the fastest path is to
            call directly — most quotes are confirmed in under five minutes.
          </p>
          <a href="tel:0494756111">
            <CTAButton variant="secondary" size="md">Call 0494 756 111</CTAButton>
          </a>
        </div>
      </div>

      <Accordion type="single" collapsible className="border-t border-ink/15">
        {FAQS.map((f, i) => (
          <AccordionItem key={i} value={`faq-${i}`} className="border-b border-ink/15">
            <AccordionTrigger className="text-left py-6 md:py-8 hover:no-underline group">
              <div className="flex items-baseline gap-5 md:gap-8 pr-4 flex-1">
                <span className="font-sans font-bold text-sm md:text-base text-ember tracking-tight shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-display text-xl md:text-2xl lg:text-3xl tracking-editorial leading-tight group-hover:text-ember transition-colors">{f.q}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-8 pl-12 md:pl-16 pr-4 md:pr-12">
              <p className="text-base md:text-lg text-char leading-relaxed max-w-3xl">{f.a}</p>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

// ─── 8. FINAL CTA ─────────────────────────────────────
const FinalCTA = () => (
  <section id="enquiry" className="relative bg-ink text-bone overflow-hidden">
    <div className="absolute inset-0 opacity-30">
      <Photo slug="hero-banquet-01" alt="" ratio="auto" className="!relative h-full w-full" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/85 to-ink" />

    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10 py-24 lg:py-36">
      <div className="grid grid-cols-12 gap-10 lg:gap-16">
        <div className="col-span-12 lg:col-span-5">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            Final step · Hand-confirmed quotes
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Ready to refresh
            <br />
            <span className="italic font-light text-ember">your menu photos?</span>
          </h2>
          <p className="mt-8 text-base md:text-lg text-bone/80 leading-relaxed max-w-md">
            Quick form below or call directly — quotes confirmed inside one
            business day. No marketing chase, no spam, no AI follow-ups.
          </p>

          <div className="mt-10 space-y-5 border-t border-bone/15 pt-8">
            <a href="tel:0494756111" className="group flex items-center gap-4">
              <Phone size={16} strokeWidth={1.6} className="text-ember" />
              <div>
                <p className="text-xs uppercase tracking-wider2 text-bone/55 mb-1 font-semibold">Direct</p>
                <p className="font-sans font-bold text-2xl tracking-tight group-hover:text-ember transition-colors">0494 756 111</p>
              </div>
            </a>
            <a href="mailto:hello@restaurantphotographer.com.au" className="group flex items-center gap-4">
              <Mail size={16} strokeWidth={1.6} className="text-ember" />
              <div>
                <p className="text-xs uppercase tracking-wider2 text-bone/55 mb-1 font-semibold">Email</p>
                <p className="font-display text-lg md:text-xl tracking-editorial group-hover:text-ember transition-colors break-all">hello@restaurantphotographer.com.au</p>
              </div>
            </a>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-7">
          <div className="bg-bone text-ink p-8 lg:p-12">
            <p className="text-xs uppercase tracking-wider2 text-ember mb-3 font-bold">Request a shoot quote</p>
            <h3 className="font-display text-2xl md:text-3xl tracking-editorial leading-tight mb-8">Tell us about your menu shoot.</h3>
            <QuoteForm />
          </div>
        </div>
      </div>
    </div>
  </section>
);
